// force-destination.ts — Force Move S3 (2026-09-29, R-099): the PLAYER force-move destination check, twinned for the browser.
//
// ft3d_force_destination_clear() (includes/functions/janus/encounter/ft3d_force_placement.php) — a player's Force Move may land in an
// occupied spot but never inside solid architecture. For the mover (compounds hand back before this runs) it refuses:
//   (a) a body whose size-tier span [z, z + base] strictly contains a shape box's floor slab at (x, y) — any box, no roof decks;
//   (b) feet below the floor, or a head above the ceiling, of the floor band at the destination (roof decks included);
//   (c) anything ft_resolve_move_destination(architecture only, walk, free elevation) would not PLACE exactly there — twinned by
//       liftDestinationClear() (lift-occupancy.ts, oracle-tested against that same PHP call), with no obstacles (PHP strips both
//       rosters: ft_projection_without_rosters) and a mesh body's too-short openings closed (ft_physical_body_navigation_shape).
// 'unknown' = the page cannot answer (no shape; a live door / breach / scene-wall overlay it does not hold near the body) → hand back.
// Oracle: tools/probes/force_destination_oracle.php → tools/test/janus-force-destination-oracle.test.mjs.
import { ceilingAt, floorAt } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { floorBandShape, liftDestinationClear, LiftUncertain } from '../inventory/lift-occupancy.js';
import { physicalBodyNavigationShape } from '../../shared/physical-creature-body.js';
import type { RestShape, Zone } from '../inventory/floor-move.js';
import { movementFootprint } from './body-geometry.js';

export interface ForceDestinationInput {
  /** ft_encounter_shape(): the shape with live door states applied (the scene's own), or the authored one with `overlayUnknown`. */
  shape: RestShape | null;
  /** ft_encounter_canvas_is_exterior(): encounter_inside !== 1. */
  canvasExterior: boolean;
  overlayUnknown: boolean;
  /** Rigid/full-radius placement controls; normal creature movement uses the ladder. */
  allowSqueeze?: boolean;
  /** The collision body (a mesh body when banked, else the size-tier cylinder) and ft_ground_occupancy_base_size (the tier). */
  body: { diameter: number; height: number; widthFt?: number; baseFt: number; mesh: boolean; squeezeSizeFt?: number };
  at: { x: number; y: number; z: number };
}
export type ForceDestination = 'clear' | 'blocked' | 'unknown';

export function forceDestinationClear(input: ForceDestinationInput): ForceDestination {
  const { shape, body } = input;
  const { x, y, z } = input.at;
  if (!shape || !Array.isArray(shape.boxes) || shape.boxes.length === 0) return 'unknown';
  if (![body.diameter, body.height, body.baseFt].every(v => Number.isFinite(v) && v > 0)) return 'unknown';
  // (a) a floor slab strictly inside the body's size-tier span
  const head = z + body.baseFt;
  for (const box of shape.boxes) {
    if (x >= box.x.min && x <= box.x.max && y >= box.y.min && y <= box.y.max
      && box.floor > z + 0.01 && box.floor < head - 0.01) return 'blocked';
  }
  // (b) the destination band's floor and ceiling
  const band = floorBandShape(shape, x, y, z);
  const probe = band?.shape ?? shape;
  const floor = floorAt(probe, x, y) as number | null, ceiling = ceilingAt(probe, x, y) as number | null;
  if ((floor !== null && z < floor - 0.01) || (ceiling !== null && head > ceiling + 0.01)) return 'blocked';
  // (c) architecture-only placement, exact
  const movement = body.mesh ? physicalBodyNavigationShape(shape as never, body.height) as unknown as RestShape : shape;
  const footprintAt = (px: number, py: number, pz: number): Zone => movementFootprint(body, px, py, pz);
  try {
    return liftDestinationClear({ shape: movement, canvasExterior: input.canvasExterior, persisted: { x, y, z }, footprintAt,
      heightFt: body.height, tierFt: body.baseFt, obstacles: () => [], overlayUnknown: input.overlayUnknown,
      allowSqueeze: input.allowSqueeze ?? !body.mesh, squeezeSizeFt: body.squeezeSizeFt }, x, y, z) ? 'clear' : 'blocked';
  } catch (error) {
    if (error instanceof LiftUncertain) return 'unknown';
    throw error;
  }
}
